import {Injectable} from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';

import {Undefinable} from '@atmi/core/models';

import {inputIsNotNullOrUndefined} from '@atmi/core/utils/operators';
import {keys} from '@sim/form/utils';


export type FieldsOptions<T> = { [P in keyof T]?: FormOptions<T> };
export type FormControlType = 'array' | 'control' | 'object';
export type FormOptions<T> =
  AbstractControlOptions &
  {
    value?: any, // TODO: Avoid any, use Z instead
    disabled?: boolean,
    required?: boolean,
    type?: FormControlType,
    controls?: FieldsOptions<T>,
    additionalInfo?: any, // TODO: Avoid any, use Z instead
  };

@Injectable()
export class FormManagerService {

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
  ) {
  }

  makeForm<T>(fields: FieldsOptions<T>, initData?: T): UntypedFormGroup {
    const groups: Partial<Record<keyof T, UntypedFormControl | UntypedFormGroup | UntypedFormArray>> = {};
    const fieldsKeys = keys(fields);

    for (const key of fieldsKeys) {
      const keyOptions = fields[key];
      if (!keyOptions) {
        continue;
      }


      const initValue = (initData && initData[key]) ||
        (inputIsNotNullOrUndefined(keyOptions.value) ? keyOptions.value : undefined);

      const disabled = keyOptions.disabled || false;


      switch (keyOptions.type || 'control') {
        case 'control':
          groups[key] = new UntypedFormControl({value: initValue, disabled}, keyOptions.validators, keyOptions.asyncValidators);
          break;
        case 'object':
          if (!keyOptions.controls) {
            console.warn('You are trying to create a form group without a controls info schema. Default FormControl will be generated');
            groups[key] = new UntypedFormControl({
                value: initValue,
                disabled
              },
              keyOptions.validators,
              keyOptions.asyncValidators);
            break;
          }

          groups[key] = this.makeForm(keyOptions.controls, initValue);

          break;
        case 'array':
          let controls: UntypedFormControl[] | UntypedFormGroup[];
          if (initValue instanceof Array && keyOptions.controls) {
            controls = initValue
              .map((v) => keyOptions.controls ? this.makeForm(keyOptions.controls, v) : undefined)
              .filter((v): v is UntypedFormGroup => v !== undefined);
          } else if (initValue instanceof Array && !keyOptions.controls) {
            controls = initValue.map((v) =>
              new UntypedFormControl({value: v, disabled}, keyOptions.validators, keyOptions.asyncValidators));
          } else {
            controls = [];
          }

          groups[key] = new UntypedFormArray(controls);

          break;
        default:
          groups[key] = new UntypedFormControl({value: initValue, disabled}, keyOptions.validators, keyOptions.asyncValidators);
      }

    }
    return this.formBuilder.group(groups);
  }

  getControls<T>(form: UntypedFormGroup) {
    return form.controls as { [P in keyof T]: AbstractControl };
  }

  setControlsValues<T>(form: Undefinable<UntypedFormGroup>, data: T) {
    if (!form) {
      throw new Error('Cannot set forms input');
    }

    const keysValues = keys(form.controls);

    for (const key of keysValues) {
      if (form.controls && form.controls[key]) {
        (form.controls[key] as AbstractControl).setValue((data as any)[key]); // FIXME: Improve typing
      }
    }

    form.updateValueAndValidity();
  }
}
